import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Flex, IconButton, Text } from '@chakra-ui/react';
import {
  Badge,
  Campaign,
  CampaignAttentionReason,
  EditOutline,
  RefreshOutline,
} from '@aslanpay/portal-common-lib';

import { campaignStatusDetails } from '.';

export interface PrideRow {
  id: string;
  /** The pride this run belongs to. Absent on a campaign with one company-wide winner. */
  groupId?: string | null;
  prideName: string;
  status: Campaign['status'];
  outcome: Campaign['outcome'];
  winner: string;
  managerIds: string[];
  needsCampaignManager: boolean;
  /** How many nominations this pride received. */
  nominationCount: number;
  /** A failed announcement that stopped somewhere it can be resumed from. */
  retryable: boolean;
  /** Why this pride wants attention, as the service worked it out. Empty where nothing does. */
  attentionReasons: CampaignAttentionReason[];
  /** How far beneath the company this pride sits, so the table can show what contains what. */
  depth: number;
}

/** The statuses past which a pride is decided, so its managers are fixed. */
const DECIDED: Campaign['status'][] = ['COMPLETED', 'CANCELLED', 'FAILED'];

function getPrideColumns(
  onSelect: (pride: PrideRow) => void,
  onManageManagers: (pride: PrideRow) => void,
  onRetry: (pride: PrideRow) => void,
  nameOf: (id: string) => string
) {
  const columnHelper = createColumnHelper<PrideRow>();

  const rowClick = {
    getCellContext: (context: CellContext<PrideRow, unknown>) => ({
      onClick: () => onSelect(context.row.original),
    }),
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PrideRow, any>[] = [
    columnHelper.accessor('prideName', {
      header: 'Pride',
      enableSorting: false,
      meta: rowClick,
      // Indented rather than nested: the rows are a campaign's prides, and a parent pride running its own
      // campaign is a row in its own right, not a heading for the ones beneath it.
      cell: ({ row, getValue }) => (
        <Flex align="center" pl={`${row.original.depth * 18}px`} gap="6px">
          {row.original.depth > 0 && (
            <Text textStyle="smallRegular" color="brand.darkGray" aria-hidden>
              └
            </Text>
          )}
          <Text textStyle="smallMedium">{getValue() as string}</Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        const details = campaignStatusDetails[row.original.status];

        return (
          <Flex align="center" gap="6px">
            <Badge value={details.label} status={details.badgeStatus} />
            {row.original.retryable && (
              <IconButton
                aria-label={`Announce ${row.original.prideName} again`}
                icon={<RefreshOutline boxSize="16px" />}
                variant="ghost"
                size="xs"
                onClick={() => onRetry(row.original)}
                data-testid={`retry-${row.original.id}`}
              />
            )}
          </Flex>
        );
      },
      meta: { ...rowClick, customColumnStyles: { width: '1%', whiteSpace: 'nowrap' } },
    }),
    columnHelper.accessor('nominationCount', {
      header: 'Nominations',
      enableSorting: false,
      cell: ({ getValue }) => (getValue() as number).toLocaleString('en-GB'),
      meta: rowClick,
    }),
    columnHelper.accessor('outcome', {
      header: 'Outcome',
      enableSorting: false,
      cell: ({ getValue }) => {
        const outcome = getValue() as Campaign['outcome'];

        if (outcome === 'WINNER') return <Badge value="Winner picked" status="active" />;
        if (outcome === 'NO_WINNER') return <Badge value="No winner" status="locked" />;

        return <Badge value="Not decided" status="warning" />;
      },
      meta: { ...rowClick, customColumnStyles: { width: '1%', whiteSpace: 'nowrap' } },
    }),
    columnHelper.accessor('winner', { header: 'Winner', enableSorting: false, meta: rowClick }),
    columnHelper.display({
      id: 'managers',
      header: 'Campaign managers',
      // No row click: this cell is where a pride's managers are changed, not a way through to the pride.
      cell: ({ row }) => {
        const pride = row.original;
        const isFixed = DECIDED.includes(pride.status);

        return (
          <Flex align="center" gap="6px">
            {pride.managerIds.length === 0 ? (
              <Badge value="Nobody to decide" status="left" />
            ) : (
              <Text textStyle="smallRegular" color="brand.black">
                {pride.managerIds.map(nameOf).join(', ')}
              </Text>
            )}
            {!isFixed && (
              <IconButton
                aria-label={`Manage who decides ${pride.prideName}`}
                icon={<EditOutline boxSize="16px" />}
                variant="ghost"
                size="xs"
                onClick={() => onManageManagers(pride)}
                data-testid={`manage-managers-${pride.id}`}
              />
            )}
          </Flex>
        );
      },
    }),
  ];

  return columns;
}

export default getPrideColumns;
